import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronLeft, Check, AlertCircle } from 'lucide-react';
import api from '@/services/api';

// Import onboarding steps
import Welcome from './onboarding/Welcome';
import TinnitusProfile from './onboarding/TinnitusProfile';
import ContextImpact from './onboarding/ContextImpact';
import CareHistory from './onboarding/CareHistory';
// Preferences removed - will be configurable later in settings

const Onboarding = ({ t }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    consent_non_medical: false,
    // Tinnitus Profile
    onset_bucket: '',
    laterality: '',
    percept_type: [],
    // Context & Impact
    triggers: [],
    worst_moments: [],
    distress_0_10: 5,
    sleep_0_10: 5,
    // Care History
    consulted: [],
    audiogram_done: 'unknown',
    hearing_aids: false,
    // Preferences removed - will be configurable later
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [subStepInfo, setSubStepInfo] = useState({ current: null, total: null });
  const [childBackHandler, setChildBackHandler] = useState(null);

  const steps = [
    { id: 'welcome', component: Welcome, title: 'Bienvenue' },
    { id: 'profile', component: TinnitusProfile, title: 'Profil' },
    { id: 'impact', component: ContextImpact, title: 'Impact' },
    { id: 'care', component: CareHistory, title: 'Suivi' },
    // Preferences step removed - will be configurable later in settings
  ];

  const progress = ((currentStep + 1) / steps.length) * 100;

  // Load draft from localStorage on mount
  useEffect(() => {
    const draft = localStorage.getItem('hearen-onboarding-draft');
    if (draft) {
      try {
        const parsedDraft = JSON.parse(draft);
        setFormData(parsedDraft.formData || formData);
        setCurrentStep(parsedDraft.currentStep || 0);
      } catch (error) {
        console.error('Error loading draft:', error);
      }
    }
  }, []);

  // Save draft to localStorage whenever formData or currentStep changes
  useEffect(() => {
    const draft = {
      formData,
      currentStep,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem('hearen-onboarding-draft', JSON.stringify(draft));
  }, [formData, currentStep]);

  const updateFormData = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const handleNext = () => {
    console.log('👆 handleNext clicked! currentStep:', currentStep, 'total steps:', steps.length);
    console.log('📊 canGoNext:', canGoNext, 'isSubmitting:', isSubmitting);
    
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
      setSubStepInfo({ current: null, total: null }); // Reset sub-step info when moving to next major step
      window.scrollTo(0, 0);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    console.log('🔙 Back button clicked, currentStep:', currentStep);
    
    // First, check if child component can handle the back action (for sub-steps)
    if (childBackHandler && childBackHandler()) {
      window.scrollTo(0, 0);
      return;
    }
    
    // Otherwise, go to previous major step
    if (currentStep > 0) {
      setCurrentStep(prev => {
        console.log('Going back from step', prev, 'to', prev - 1);
        return prev - 1;
      });
      setSubStepInfo({ current: null, total: null }); // Reset sub-step info when moving to previous major step
      setChildBackHandler(null); // Reset child back handler
      window.scrollTo(0, 0);
    } else {
    }
  };

  const handleLater = async () => {
    // Increment defer count
    const deferCount = parseInt(localStorage.getItem('hearen-onboarding-defers') || '0');
    
    if (deferCount >= 2) {
      alert(t?.onboarding_max_defers || 'Veuillez compléter votre profil pour personnaliser votre expérience.');
      return;
    }
    
    localStorage.setItem('hearen-onboarding-defers', String(deferCount + 1));
    navigate('/');
  };

  const handleSubmit = () => {
    // Build profile data
    const profileData = {
      onboarding: {
        version: 1,
        completed: true,
        completed_at: new Date().toISOString(),
        consent_non_medical: formData.consent_non_medical,
        defers: parseInt(localStorage.getItem('hearen-onboarding-defers') || '0'),
      },
      tinnitus_profile: {
        onset: {
          bucket: formData.onset_bucket,
          approx_date: null,
        },
        laterality: formData.laterality,
        percept_type: formData.percept_type,
        triggers: formData.triggers,
        worst_moments: formData.worst_moments,
        distress_0_10: formData.distress_0_10,
        sleep_0_10: formData.sleep_0_10,
      },
      care: {
        consulted: formData.consulted,
        audiogram_done: formData.audiogram_done,
        hearing_aids: formData.hearing_aids,
      },
      preferences: {
        primary_goal: '',
        sound_sensitivity: 'medium',
        favorite_categories: [],
      },
    };

    // Submit to backend in background (fire and forget)
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    
    fetch(`${backendUrl}/api/user/profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(profileData),
    }).catch(() => {
      // Silent fail - data will be saved on next login check
    });

    // Clear localStorage
    localStorage.removeItem('hearen-onboarding-draft');
    localStorage.removeItem('hearen-onboarding-defers');

    // Force navigation using window.location (bypasses React Router cache)
    window.location.href = '/get-started';
  };

  const handleSubStepChange = (current, total) => {
    setSubStepInfo({ current, total });
  };
  
  const handleChildBackPress = (backHandler) => {
    setChildBackHandler(() => backHandler);
  };

  const StepComponent = steps[currentStep].component;
  const canGoNext = currentStep === 0 ? formData.consent_non_medical : true;
  
  // Hide main "Continuer" button when inside a multi-step component that's not on its last sub-step
  // EXCEPT if we're on the last major step AND last sub-step, then show "Terminer"
  const isLastStep = currentStep === steps.length - 1;
  const isOnLastSubStep = subStepInfo.current === subStepInfo.total;
  const shouldHideMainButton = subStepInfo.current !== null && subStepInfo.current < subStepInfo.total && !isOnLastSubStep;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
        <div className="h-1 bg-gray-200">
          <div
            className="h-full bg-blue-500 transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={handleBack}
            disabled={currentStep === 0}
            className={`p-2 rounded-full transition-all ${
              currentStep === 0
                ? 'text-gray-300 cursor-not-allowed opacity-50'
                : 'text-gray-700 hover:bg-gray-100 active:bg-gray-200'
            }`}
            aria-label="Retour"
          >
            <ChevronLeft size={24} strokeWidth={2.5} />
          </button>
          
          <div className="text-center">
            <p className="text-sm font-medium text-gray-900">
              {steps[currentStep].title}
            </p>
            <p className="text-xs text-gray-500">
              {currentStep + 1} / {steps.length}
            </p>
          </div>

          {currentStep > 0 && currentStep < steps.length - 1 && (
            <button
              onClick={handleLater}
              className="text-sm text-blue-500 hover:text-blue-600 font-medium"
            >
              Plus tard
            </button>
          )}
          {(currentStep === 0 || currentStep === steps.length - 1) && (
            <div className="w-16" />
          )}
        </div>
      </div>

      {/* Content */}
      <div className="pt-24 pb-56 px-6">
        <div className="max-w-2xl mx-auto">
          <StepComponent
            formData={formData}
            updateFormData={updateFormData}
            t={t}
            onSubStepChange={handleSubStepChange}
            onBackPress={handleChildBackPress}
          />

          {error && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-2xl flex items-start gap-3">
              <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>
      </div>

      {/* Navigation Footer - Hidden when inside multi-step components */}
      {!shouldHideMainButton && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg" style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 100px)' }}>
          <div className="max-w-2xl mx-auto px-6 py-6">
            <button
              onClick={handleNext}
              disabled={!canGoNext || isSubmitting}
              className={`w-full py-4 rounded-2xl font-semibold text-white text-lg transition-all ${
                !canGoNext || isSubmitting
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-blue-500 hover:bg-blue-600 active:scale-[0.98] shadow-lg hover:shadow-xl'
              }`}
            >
              {isSubmitting ? (
                <span>Enregistrement...</span>
              ) : currentStep === steps.length - 1 ? (
                <span className="flex items-center justify-center gap-2">
                  Terminer
                  <Check size={20} />
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  Continuer
                  <ChevronRight size={20} />
                </span>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Onboarding;
